import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { ShieldCheck, Loader2 } from "lucide-react";

const BUREAUS = [
  { id: "cibil", name: "TransUnion CIBIL", fields: ["api_key", "member_id"] },
  { id: "experian", name: "Experian India", fields: ["api_key", "client_id", "client_secret"] },
  { id: "equifax", name: "Equifax India", fields: ["username", "password", "api_key"] },
  { id: "crif", name: "CRIF High Mark", fields: ["client_id", "client_secret", "api_key"] },
];

export default function BankFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", short_name: "", head_office_address: "", ifsc_prefix: "",
    regions: "East,West,Central", product_catalog: "CC,TL,MUDRA_SHISHU,MUDRA_KISHORE,MUDRA_TARUN,SME_CC,STAND_UP_INDIA,WC_NAYAK,PROJECT,CGTMSE_HYBRID,PERSONAL_LOAN,HOUSING_LOAN",
    contact_email: "", contact_phone: "",
    credit_bureaus: BUREAUS.reduce((acc, b) => ({ ...acc, [b.id]: { enabled: false, sandbox: true, api_key: "", member_id: "", client_id: "", client_secret: "", username: "", password: "", base_url: "" } }), {}),
    is_active: true,
  });
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState({});

  useEffect(() => {
    if (editing) {
      (async () => {
        try {
          const r = await api.get(`/super-admin/banks/${id}`);
          const b = r.data.bank;
          setForm({
            name: b.name || "", short_name: b.short_name || "", head_office_address: b.head_office_address || "", ifsc_prefix: b.ifsc_prefix || "",
            regions: (b.regions || []).join(","), product_catalog: (b.product_catalog || []).join(","),
            contact_email: b.contact_email || "", contact_phone: b.contact_phone || "",
            credit_bureaus: BUREAUS.reduce((acc, x) => ({ ...acc, [x.id]: { ...(b.credit_bureaus?.[x.id] || { enabled: false, sandbox: true }) } }), {}),
            is_active: b.is_active !== false,
          });
        } catch (e) { toast.error("Failed to load bank"); }
      })();
    }
  }, [id, editing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBureau = (bureau, k, v) => setForm(f => ({ ...f, credit_bureaus: { ...f.credit_bureaus, [bureau]: { ...f.credit_bureaus[bureau], [k]: v } } }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name) return toast.error("Bank name required");
    setBusy(true);
    try {
      const payload = { ...form, regions: form.regions.split(",").map(s => s.trim()).filter(Boolean), product_catalog: form.product_catalog.split(",").map(s => s.trim()).filter(Boolean) };
      if (editing) await api.patch(`/super-admin/banks/${id}`, payload);
      else await api.post("/super-admin/banks", payload);
      toast.success(editing ? "Bank updated" : "Bank onboarded");
      navigate("/super-admin");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const testBureau = async (bureau) => {
    if (!editing) return toast.message("Save the bank first to test bureau connection");
    try {
      const r = await api.post(`/super-admin/banks/${id}/test-bureau/${bureau}`);
      setTested(t => ({ ...t, [bureau]: r.data }));
      toast.success(`${bureau.toUpperCase()}: ${r.data.status}`);
    } catch (e) { toast.error("Test failed"); }
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <SectionHeader title={editing ? "Manage Bank" : "Onboard New Bank"} subtitle="Tenant configuration including credit bureau API integration." />
      <form onSubmit={submit} className="space-y-4">
        <Card className="p-5 space-y-3">
          <h3 className="text-sm font-semibold heading">Bank Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Bank Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="bank-form-name" /></Field>
            <Field label="Short Name"><Input value={form.short_name} onChange={(e) => set("short_name", e.target.value)} placeholder="e.g., DRB" /></Field>
            <Field label="IFSC Prefix"><Input value={form.ifsc_prefix} onChange={(e) => set("ifsc_prefix", e.target.value)} placeholder="e.g., DRBK0" /></Field>
            <Field label="Contact Email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Contact Phone"><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="Active"><div className="h-10 flex items-center"><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /></div></Field>
            <Field label="Head Office Address" className="md:col-span-2"><Textarea rows={2} value={form.head_office_address} onChange={(e) => set("head_office_address", e.target.value)} /></Field>
            <Field label="Regions (comma-separated)" className="md:col-span-2"><Input value={form.regions} onChange={(e) => set("regions", e.target.value)} /></Field>
            <Field label="Product Catalog (comma-separated codes)" className="md:col-span-2"><Input value={form.product_catalog} onChange={(e) => set("product_catalog", e.target.value)} /></Field>
          </div>
        </Card>
        <Card className="p-5 space-y-3">
          <div>
            <h3 className="text-sm font-semibold heading">Credit Bureau API Configuration</h3>
            <p className="text-xs text-muted-foreground">Configure scoring APIs per bureau. API keys are encrypted at rest (AES-GCM). Sandbox mode uses mocked responses until real credentials are provided.</p>
          </div>
          {BUREAUS.map(b => (
            <Card key={b.id} className="p-4 border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Switch checked={form.credit_bureaus[b.id]?.enabled} onCheckedChange={(v) => setBureau(b.id, "enabled", v)} data-testid={`bureau-${b.id}-toggle`} />
                  <div>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-[11px] text-muted-foreground">Required: {b.fields.join(", ")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={form.credit_bureaus[b.id]?.sandbox ? "text-warning border-warning/30" : "text-success border-success/30"}>{form.credit_bureaus[b.id]?.sandbox ? "Sandbox" : "Production"}</Badge>
                  <Button type="button" size="sm" variant="outline" onClick={() => testBureau(b.id)} data-testid={`bureau-${b.id}-test`}>Test</Button>
                  {tested[b.id] && <Badge variant="outline" className="text-success border-success/30"><ShieldCheck className="h-3 w-3 mr-1 inline" />{tested[b.id].status}</Badge>}
                </div>
              </div>
              {form.credit_bureaus[b.id]?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border">
                  {b.fields.includes("api_key") && <Field label="API Key"><Input type="password" value={form.credit_bureaus[b.id]?.api_key || ""} onChange={(e) => setBureau(b.id, "api_key", e.target.value)} placeholder="••••••••" /></Field>}
                  {b.fields.includes("member_id") && <Field label="Member ID"><Input value={form.credit_bureaus[b.id]?.member_id || ""} onChange={(e) => setBureau(b.id, "member_id", e.target.value)} /></Field>}
                  {b.fields.includes("client_id") && <Field label="Client ID"><Input value={form.credit_bureaus[b.id]?.client_id || ""} onChange={(e) => setBureau(b.id, "client_id", e.target.value)} /></Field>}
                  {b.fields.includes("client_secret") && <Field label="Client Secret"><Input type="password" value={form.credit_bureaus[b.id]?.client_secret || ""} onChange={(e) => setBureau(b.id, "client_secret", e.target.value)} /></Field>}
                  {b.fields.includes("username") && <Field label="Username"><Input value={form.credit_bureaus[b.id]?.username || ""} onChange={(e) => setBureau(b.id, "username", e.target.value)} /></Field>}
                  {b.fields.includes("password") && <Field label="Password"><Input type="password" value={form.credit_bureaus[b.id]?.password || ""} onChange={(e) => setBureau(b.id, "password", e.target.value)} /></Field>}
                  <Field label="Base URL"><Input value={form.credit_bureaus[b.id]?.base_url || ""} onChange={(e) => setBureau(b.id, "base_url", e.target.value)} placeholder="https://api.…" /></Field>
                  <Field label="Sandbox Mode"><div className="h-10 flex items-center"><Switch checked={form.credit_bureaus[b.id]?.sandbox} onCheckedChange={(v) => setBureau(b.id, "sandbox", v)} /></div></Field>
                </div>
              )}
            </Card>
          ))}
        </Card>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate("/super-admin")}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="bank-form-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Update Bank" : "Onboard Bank")}</Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (<div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}
